import { useCallback, useEffect, useRef, useState } from "react";

// Browser SpeechRecognition wrapper — start/stop + interim transcript.
// Returns { supported, listening, transcript, start, stop, error }.
export function useSpeechRecognition(lang = "fr") {
  const recRef = useRef(null);
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const SR =
      window.SpeechRecognition ||
      window.webkitSpeechRecognition ||
      null;
    if (!SR) {
      setSupported(false);
      return;
    }
    setSupported(true);
    const rec = new SR();
    rec.lang = lang === "fr" ? "fr-FR" : "en-US";
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.onresult = (event) => {
      let text = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        text += event.results[i][0].transcript;
      }
      setTranscript(text.trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = (e) => {
      setError(e.error || "recognition-error");
      setListening(false);
    };
    recRef.current = rec;
    return () => {
      try {
        rec.stop();
      } catch {
        // ignore
      }
    };
  }, [lang]);

  const start = useCallback(() => {
    setError("");
    setTranscript("");
    if (!recRef.current) return;
    try {
      recRef.current.start();
      setListening(true);
    } catch {
      // ignore double-start
    }
  }, []);

  const stop = useCallback(() => {
    if (!recRef.current) return;
    try {
      recRef.current.stop();
    } catch {
      // ignore
    }
    setListening(false);
  }, []);

  return { supported, listening, transcript, start, stop, error };
}
